import { useState, useMemo, useEffect } from 'react';
import { debounce, isEqual } from 'lodash';
import { Descendant, Element as SlateElement, Text } from 'slate';
import { Channel, UpdateCampaignInput } from '@/gql/graphql';
import { BASE_INITIAL_VALUE } from '@/components/sms-editor/types';
import { Campaign } from './use-campaigns';
import { useCampaignWizard } from '../create/context/campaign-wizard-context';

interface CampaignVariantContent {
  editorState: Descendant[];
}

interface UseVariantManagementProps {
  campaignId: string | null;
  campaign: Campaign | undefined;
  onValidityChange?: (isValid: boolean) => void;
}

interface UseVariantManagementReturn {
  activeVariantId: string | undefined;
  activeVariant: NonNullable<Campaign['variants'][number]> | null;
  variantName: string;
  editorState: Descendant[];
  handleVariantSelect: (variantId: string, variantName: string) => void;
  handleVariantNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEditorChange: (newEditorState: Descendant[]) => void;
}

// Type guard to check if content matches CampaignVariantContent structure
function isCampaignVariantContent(content: unknown): content is CampaignVariantContent {
  return (
    typeof content === 'object' &&
    content !== null &&
    'editorState' in content &&
    Array.isArray((content as CampaignVariantContent).editorState)
  );
}

export function useVariantManagement({
  campaignId,
  campaign,
  onValidityChange
}: UseVariantManagementProps): UseVariantManagementReturn {
  const { updateVariants, activeVariantId, selectVariant, localVariants, updateLocalVariant } =
    useCampaignWizard();
  const [editorState, setEditorState] = useState<Descendant[]>(BASE_INITIAL_VALUE);

  // Get active variant from campaign data
  const activeVariant = useMemo(() => {
    if (!campaign?.variants.length) return null;
    const variant = activeVariantId
      ? campaign.variants.find((v) => v.variantId === activeVariantId)
      : campaign.variants[0];
    return variant ?? null;
  }, [campaign?.variants, activeVariantId]);

  // Effect to load editor state ONLY when the activeVariantId changes (initial load or selection)
  useEffect(() => {
    if (!activeVariantId) {
      setEditorState(BASE_INITIAL_VALUE);
      return;
    }

    const selectedVariantData = localVariants[activeVariantId];
    let newEditorState = BASE_INITIAL_VALUE;

    if (selectedVariantData?.content && isCampaignVariantContent(selectedVariantData.content)) {
      newEditorState = selectedVariantData.content.editorState;
    }

    // Only update if the state for the selected variant is different
    // This prevents unnecessary updates if switching back/forth quickly
    if (!isEqual(editorState, newEditorState)) {
      setEditorState(newEditorState);
    }
  }, [activeVariantId]);

  // Helper function to create updated variants array
  const updateVariant = (
    updates: Partial<{
      content: CampaignVariantContent;
      variantName: string;
      channel?: Channel;
    }>
  ): UpdateCampaignInput['variants'] => {
    if (!campaign?.variants || !activeVariant) return [];

    return campaign.variants.map((v) =>
      v.variantId === activeVariant.variantId
        ? {
            id: v.variantId,
            channel: updates.channel ?? v.channel,
            variantName: updates.variantName ?? v.variantName,
            splitWeight: v.splitWeight,
            content:
              updates.content ??
              (v.content && isCampaignVariantContent(v.content) ? v.content : undefined)
          }
        : {
            id: v.variantId,
            channel: v.channel,
            variantName: v.variantName,
            splitWeight: v.splitWeight,
            content: v.content && isCampaignVariantContent(v.content) ? v.content : undefined
          }
    );
  };

  // Debounced save function for editor state
  const debouncedSaveContent = useMemo(
    () =>
      debounce(async (newEditorState: Descendant[]) => {
        if (!campaignId || !activeVariant || !campaign) return;

        try {
          const content: CampaignVariantContent = {
            editorState: newEditorState
          };

          // Update local state immediately
          updateLocalVariant(activeVariant.variantId, { content });

          // Update server state
          const updatedVariants = updateVariant({
            content,
            channel: Channel.Sms
          });

          await updateVariants(updatedVariants);
        } catch (_error) {
          // Error is handled by context
        }
      }, 1000),
    [campaignId, activeVariant, campaign, updateVariants, updateLocalVariant]
  );

  // Debounced save function for variant name
  const debouncedSaveName = useMemo(
    () =>
      debounce(async (newName: string) => {
        if (!campaignId || !activeVariant || !campaign) return;
        try {
          const updatedVariants = updateVariant({ variantName: newName });
          await updateVariants(updatedVariants);
        } catch (_error) {
          // Error handled by context
        }
      }, 1000), // Use the same debounce time or adjust as needed
    [campaignId, activeVariant, campaign, updateVariants]
  );

  const handleVariantSelect = (variantId: string, newVariantName: string) => {
    selectVariant(variantId);
    updateLocalVariant(variantId, { variantName: newVariantName });
  };

  const handleVariantNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!activeVariant) return;
    const newName = e.target.value;
    updateLocalVariant(activeVariant.variantId, { variantName: newName });
    void debouncedSaveName(newName);
  };

  const handleEditorChange = (newEditorState: Descendant[]) => {
    // Update local editor state immediately
    setEditorState(newEditorState);

    // Optimistic update to localVariants (for persistence across switches)
    if (activeVariant?.variantId) {
      const content: CampaignVariantContent = { editorState: newEditorState };
      updateLocalVariant(activeVariant.variantId, { content });
    }

    // Update step validation based on content
    const hasContent = newEditorState.some((node: Descendant) => {
      if (!SlateElement.isElement(node) || node.type !== 'paragraph') return false;
      return node.children.some((child) => Text.isText(child) && child.text.trim() !== '');
    });
    onValidityChange?.(hasContent);

    // Debounced API update
    void debouncedSaveContent(newEditorState);
  };

  // Get variant name from local state if available, otherwise from server state
  const variantName = activeVariant
    ? (localVariants[activeVariant.variantId]?.variantName ?? activeVariant.variantName)
    : '';

  return {
    activeVariantId,
    activeVariant,
    variantName,
    editorState,
    handleVariantSelect,
    handleVariantNameChange,
    handleEditorChange
  };
}
